/*
 * מתמטיקה שלמה בלבד.
 *
 * Math.sqrt / sin / cos / pow ואחיהם אסורים בכל הקוד הטהור: התקן מתיר להם דיוק שונה
 * בין מימושים, וכל הפרש של ביט אחד נכנס למצב המשחק, משם ל-hash, ומשם ל-desync בין
 * שני שחקנים או בין הרצה להרצה בטסטים. src/sim/purity.test.ts אוכף את האיסור.
 *
 * ארבע פעולות החשבון ו-Math.clz32 כן מותרות: IEEE-754 מגדיר אותן כמעוגלות נכון,
 * ולכן הן זהות בכל מנוע.
 */

/**
 * שורש ריבועי שלם (עיגול כלפי מטה). ניוטון על שלמים.
 *
 * הניחוש ההתחלתי חייב להיות גדול-שווה לשורש — ניוטון כאן מתכנס כלפי מטה, וניחוש
 * קטן מדי עוצר מיד ומחזיר אותו כמו שהוא (isqrt(25) החזיר 4).
 */
export function isqrt(n: number): number {
    if (n <= 0) return 0;
    if (n < 4) return 1;

    let x: number;
    if (n < 0x100000000) {
        // 2 בחזקת חצי מספר הביטים, מעוגל כלפי מעלה — תמיד גדול-שווה לשורש.
        x = 1 << (((32 - Math.clz32(n)) + 1) >> 1);
    } else {
        // מעבר ל-32 ביט אי אפשר להסתמך על clz32 (הוא ממיר ל-uint32). הכפלה עד שעוברים.
        x = 1;
        while (x * x <= n) x *= 2;
    }

    for (;;) {
        // Math.floor ולא >>1: x עלול לחרוג מ-32 ביט על מפות גדולות מאוד.
        const next = Math.floor((x + Math.floor(n / x)) / 2);
        if (next >= x) return x;
        x = next;
    }
}

/** ריבוע המרחק. עדיף על idist כשרק משווים לטווח — אין בו שורש בכלל. */
export function dist2(ax: number, ay: number, bx: number, by: number): number {
    const dx = ax - bx;
    const dy = ay - by;
    return dx * dx + dy * dy;
}

/** מרחק אוקלידי מעוגל כלפי מטה. */
export function idist(ax: number, ay: number, bx: number, by: number): number {
    return isqrt(dist2(ax, ay, bx, by));
}

export function iclamp(value: number, min: number, max: number): number {
    return value < min ? min : value > max ? max : value;
}

/** מרחק אוקטילי — ההיוריסטיקה הנכונה לרשת שמתירה תנועה באלכסון. */
export function octile(dx: number, dy: number, straightCost: number, diagonalCost: number): number {
    const ax = dx < 0 ? -dx : dx;
    const ay = dy < 0 ? -dy : dy;
    const lo = ax < ay ? ax : ay;
    const hi = ax < ay ? ay : ax;
    return straightCost * (hi - lo) + diagonalCost * lo;
}
